import { createAsset, createLumen, getTokensFromCache } from 'helpers/token';
import { truncateString } from 'helpers/truncate-string';

import { SorobanService } from 'services/globalServices';
import { isValidContract } from 'services/stellar/utils/validators';

import { Asset } from 'types/stellar';
import { ClassicToken, Token, TokenType } from 'types/token';

import { getNetworkPassphrase } from './env';

// Re-exported from constants to keep the historical import path
// (`helpers/assets`) working. The implementation lives next to
// ENV_CLASSIC_ASSETS_CONFIG and stays dependency-light, so importing it from
// constant modules at init time is cycle-safe.
export { getEnvClassicAssetData } from 'constants/assets-env';

export const getAssetString = (asset: Token): string => {
    if (asset.type === TokenType.soroban) {
        return asset.contract;
    }
    if (asset?.isNative?.()) {
        return 'native';
    }
    return `${asset.code}:${asset.issuer}`;
};

export const getStellarAsset = (code: string, issuer: string): Asset => {
    if (!issuer) {
        return createLumen();
    }

    return createAsset(code, issuer);
};

export const getAssetFromString = (str: string, onUpdateCB?: (token: Token) => void): Token => {
    if (isValidContract(str)) {
        const result = { contract: str, type: TokenType.soroban } as Token;

        const cache = getTokensFromCache();
        const cachedToken = cache?.find(({ contract }) => contract === str);

        if (cachedToken) {
            if (onUpdateCB) {
                onUpdateCB(cachedToken);
            }
            return cachedToken;
        }

        SorobanService.token
            .parseTokenContractId(str)
            .then((res: Token) => {
                Object.assign(result, res);
                if (onUpdateCB) {
                    onUpdateCB(result);
                }
            })
            .catch(() => {
                // Contract is missing/archived on this network — keep a
                // readable stub instead of crashing the page
                Object.assign(result, {
                    code: truncateString(str, 4),
                    name: 'Unknown token',
                    decimal: 7,
                });
                if (onUpdateCB) {
                    onUpdateCB(result);
                }
            });
        return result;
    }
    if (str === 'native') {
        const asset: ClassicToken = createLumen() as ClassicToken;

        asset.type = TokenType.classic;
        asset.contract = asset.contractId(getNetworkPassphrase());
        asset.decimal = 7;
        if (onUpdateCB) {
            onUpdateCB(asset);
        }
        return asset;
    }

    const [code, issuer] = str.split(':');

    const asset: ClassicToken = createAsset(code, issuer) as ClassicToken;
    asset.type = TokenType.classic;
    asset.contract = asset.contractId(getNetworkPassphrase());
    asset.decimal = 7;
    if (onUpdateCB) {
        onUpdateCB(asset);
    }
    return asset;
};

/**
 * Restores [asset1, asset2] tokens of a market pair.
 * Prefers pre-resolved `asset1_token`/`asset2_token` (see enrichPairsWithTokens),
 * reviving classic tokens into Asset instances after JSON (de)serialization.
 */
export const getMarketPairTokens = (pair: {
    asset1: string;
    asset2: string;
    asset1_token?: Token;
    asset2_token?: Token;
}): [Token, Token] => {
    const revive = (token: Token | undefined, assetString: string): Token => {
        if (!token) {
            return getAssetFromString(assetString);
        }

        return token.type === TokenType.soroban ? token : createAsset(token.code, token.issuer);
    };

    return [revive(pair.asset1_token, pair.asset1), revive(pair.asset2_token, pair.asset2)];
};
